import { createRouter, createWebHistory } from 'vue-router'
import { trans } from 'laravel-vue-i18n'
import { useBookingStore } from '@/js/booking/booking-search-store'
import Storage from '@/js/global/localstorage'


const router = createRouter({
    history: createWebHistory('/' + window.evp.currentLocale),
    routes: [
        {
            path: '/Booking/Institutions',
            name: 'Institutions',
            component: () => import('@/components/booking/views/Institutions.vue'),
            beforeEnter: () => {
                Storage.setItem('BookingSearch:section', 'institutions')
            }
        },
        {
            path: '/Booking/Institution/:institutionCode',
            name: 'InstitutionPage',
            component: () => import('@/components/booking/views/InstitutionPage.vue'),
        },
        {
            path: '/Booking/Institution/:institutionCode/Map',
            name: 'InstitutionPageMap',
            component: () => import('@/components/booking/views/InstitutionPage.vue'),
        },
        {
            path: '/Booking/Services',
            name: 'Services',
            component: () => import('@/components/booking/views/Services.vue'),
            beforeEnter: () => {
                Storage.setItem('BookingSearch:section', 'services')
            }
        },
        {
            path: '/Booking/Specialists',
            name: 'Specialists',
            component: () => import('@/components/booking/views/Specialists.vue'),
            beforeEnter: () => {
                Storage.setItem('BookingSearch:section', 'specialists')
            }
        },
        {
            path: '/Booking/Telemedicine',
            name: 'Telemedicine',
            component: () => import('@/components/booking/views/TelemedicineSpecialistsAndServices.vue'),
            beforeEnter: () => {
                Storage.setItem('BookingSearch:NavigatingTelemedicine', true)
            }
        },
        {
            path: '/Booking/FavoriteSpecialists',
            name: 'FavoriteSpecialists',
            component: () => import('@/components/booking/views/FavoriteSpecialists.vue'),
        },
        {
            path: '/Booking/AvailableTimeSlots',
            name: 'AvailableTimeSlots',
            component: () => import('@/components/booking/views/AvailableTimeSlots.vue'),
            beforeEnter: to => {
                // Ja trūkst kāds no nepieciešamajiem parametriem, pāradresē atpakaļ
                // uz lapu, kur tos var izvēlēties.
                if (!to.query.SpecialistCode && !to.query.ServiceCode) {
                    if (to.query.InstitutionCode) {
                        return {
                            name: 'InstitutionPage',
                            params: {
                                institutionCode: to.query.InstitutionCode
                            }
                        }

                    } else {
                        return {
                            name: 'Institutions'
                        }
                    }

                } else if (!to.query.SpecialistCode && !to.query.InstitutionCode) {
                    return {
                        name: 'Institutions',
                        query: {
                            ServiceCode: to.query.ServiceCode
                        }
                    }
                }
            }
        },
        {
            path: '/Booking/:section/Map',
            name: 'InstitutionMap',
            component: () => import('@/components/booking/views/InstitutionMap.vue'),
        },
        {
            // Gan priekš /Booking, gan nepareizām adresēm, kas tikušas cauri laravel rūterim līdz šejienei.
            path: '/:pathMatch(.*)*',
            redirect: () => {
                switch (Storage.getItem('BookingSearch:section')) {
                    case 'services':
                        return { name: 'Services' }
                    case 'specialists':
                        return { name: 'Specialists' }
                    default:
                        return { name: 'Institutions' }
                }
            },
        },
    ],
    scrollBehavior(to, from, savedPosition) {
        return {
            top: savedPosition?.top ?? 0,
            behavior: 'auto',
        }
    },
})


router.beforeEach((to) => {
    const bookingStore = useBookingStore()

    // Atiestata meklēšanas filtrus, ja iepriekš navigācija notikusi telemedicīnas sadaļā
    if (Storage.getBoolean('BookingSearch:NavigatingTelemedicine') && !bookingStore.isTelemedicineSelected()) {
        Storage.setItem('BookingSearch:NavigatingTelemedicine', false)
        bookingStore.resetFilter()
    }

    /**
     * Atiestata izvēlēto iestādi/pakalpojumu/ārstu, ja nākamajā skatā tas vairs nav aktuāls.
     */

    if (! to.query.InstitutionCode) {
        bookingStore.selectedInstitution = null
    }

    if (! to.query.ServiceCode) {
        bookingStore.selectedService = null
    }

    if (! to.query.SpecialistCode) {
        bookingStore.selectedSpecialist = null
    }

    // Filtrē tikai neklātienes laikus, ja parādāma telemedicīnas sadaļa
    if (to.name == 'Telemedicine') {
        bookingStore.browseTelemedicine()
    }
})

router.afterEach((to, from) => {
    Storage.setItem('BookingSearch:LastVisit', new Date())

    // Notīra paziņojumus pēc katras pāradresācijas, izņemot pašu pirmo, kas notiek uz lapas ielādes.
    if (from.name) {
        window.evp.initialMessages.warning = null
        window.evp.initialMessages.success = null
        window.evp.initialMessages.errors = []
        window.clearNotifications()
    }
})


export default router


/**
 * Uzstāda lapas meta title. Piemēram, `Pieraksti - Anna Daktere - eVeselībasPunkts`
 */
export function updatePageTitle() {
    const bookingStore = useBookingStore()
    let sectionTitle

    if (bookingStore.selectedSpecialist) {
        sectionTitle = bookingStore.selectedSpecialist.displayName

    } else if (bookingStore.selectedService) {
        sectionTitle = bookingStore.selectedService.displayName

    } else if (bookingStore.selectedInstitution) {
        sectionTitle = bookingStore.selectedInstitution.displayName
    }


    if (sectionTitle) {
        document.title = trans('global.AppointmentTitle') + ` - ${sectionTitle} - ` + trans('global.PortalName')

    } else {
        document.title = trans('global.AppointmentTitle') + ` - ` + trans('global.PortalName')
    }
}


/**
 * Sagatavo objektu priekš <router-link :to="..."> uz norādīto sadaļu un noņem tukšos query parametrus, ja tādi padoti.
 * 
 * @param {String} name
 * @param {Object} options
 */
export function getRouteLocation(name, options = {}) {
    const bookingStore = useBookingStore()

    if (options.query) {
        for (const [key, value] of Object.entries(options.query)) {
            if (! value) {
                delete options.query[key]
            }
        }
    } else {
        options.query = {}
    }

    // Saglabā embedded mode starp navigācijām.
    if (bookingStore.route.query.EmbeddedCode) {
        options.query.EmbeddedCode = bookingStore.route.query.EmbeddedCode
    }

    return { name, ...options }
}


/**
 * Pāradresē lietotāju uz norādīto sadaļu un noņem tukšos query parametrus, ja tādi padoti.
 * 
 * @param {String} name
 * @param {Object} options
 */
export function goTo(name, options = {}) {
    const routeLocation = getRouteLocation(name, options)
    const to = router.resolve(routeLocation)

    // Lietotājs pārvietojas uz lapu, kurā bija pirms tam
    // Simulē pārlūka 'back' pogas uzvedību, ja lietotājs noklikšķinājis uz BackLink
    if (options.mockBackButton && to.fullPath == window.history.state.back) {
        router.back()

    } else {
        router.push(to)
    }
}
